import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Plus,
  Trash2,
  Cloud,
  Sprout,
  Droplets,
  Recycle,
  Target,
  AlertTriangle,
  type LucideIcon,
} from "lucide-react";
import { useWizard } from "@/contexts/WizardContext";
import { LockedBanner } from "../common/LockedBanner";
import { ProgressCard } from "../common/ProgressCard";
import type { StoryEnvironmentData, SustainabilityTarget } from "@/contexts/wizard/types";
import { CardPanelHeader } from "../common/CardPanelHeader";
import { SectionCard } from "../common/SectionCard";
import { VoiceTextareaField } from "../common/VoiceTextareaField";

interface StoryEnvironmentCardProps {
  onCollapse: () => void;
}

type EnvironmentFieldKey = Exclude<keyof StoryEnvironmentData, "c3Targets">;

type SectionConfig = {
  id: string;
  index: string;
  title: string;
  description?: string;
  icon: LucideIcon;
  fields?: Array<{
    key: EnvironmentFieldKey;
    id: string;
    label: string;
    placeholder: string;
    rows?: number;
  }>;
};

function TargetTextField({
  id,
  label,
  placeholder,
  value,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-400">
        {label}
      </Label>
      <Input
        id={id}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="form-input h-11"
      />
    </div>
  );
}

export function StoryEnvironmentCard({ onCollapse }: StoryEnvironmentCardProps) {
  const {
    storyEnvironmentData: data,
    updateStoryEnvironmentData: update,
    forceSave,
    saveStatus,
    isLocked,
    isReadOnly,
    storyEnvironmentCompletion,
  } = useWizard();

  const disabled = isLocked || isReadOnly;

  const addTarget = () => {
    if (data.c3Targets.length >= 3) return;

    const newTarget: SustainabilityTarget = {
      id: `target-${Date.now()}`,
      target: "",
      baseline: "",
      deadline: "",
      transitionActions: "",
      responsibilityTracking: "",
      constraintsRisks: "",
    };

    update({ c3Targets: [...data.c3Targets, newTarget] });
  };

  const removeTarget = (id: string) => {
    update({ c3Targets: data.c3Targets.filter((target) => target.id !== id) });
  };

  const updateTarget = (id: string, updates: Partial<SustainabilityTarget>) => {
    update({
      c3Targets: data.c3Targets.map((target) => (target.id === id ? { ...target, ...updates } : target)),
    });
  };

  const narrativeSections: SectionConfig[] = [
    {
      id: "b3",
      index: "B3",
      title: "B3 Energy & GHG Emissions",
      icon: Cloud,
      fields: [
        {
          key: "b3EmissionsDrivers",
          id: "story-environment-b3-emissions-drivers",
          label: "What are the main drivers of your Scope 1 & 2 emissions this year? (Mention the biggest sources and why)",
          placeholder: "e.g. Our main emission sources are vehicle fleet diesel (45% of total) and electricity for manufacturing (40%). Energy use increased this year due to production expansion...",
        },
        {
          key: "b3ReductionActions",
          id: "story-environment-b3-reduction-actions",
          label: "What did you do to reduce energy use or emissions? (Include any numbers if possible: kWh saved, efficiency upgrade, solar installed, etc.)",
          placeholder: "e.g. Installed LED lighting across facilities saving 12,000 kWh annually. Upgraded boiler efficiency from 75% to 90%. Began transition to hybrid fleet vehicles...",
        },
        {
          key: "b3NextPriorities",
          id: "story-environment-b3-next-priorities",
          label: "What are your next priorities and targets for the next 12 to 24 months?",
          placeholder: "e.g. Install 50kW solar panels by Q3 2026 to offset 30% of electricity. Complete fleet transition to electric/hybrid vehicles. Target 20% reduction in energy intensity...",
          rows: 3,
        },
        {
          key: "b3DataLimitations",
          id: "story-environment-b3-data-limitations",
          label: "What are the main limitations of this year's data, and what will you improve next year?",
          placeholder: "e.g. Some vehicle fuel data estimated from receipts rather than meter readings. Next year we'll implement fuel card tracking for all vehicles. Refrigerant data incomplete for older units...",
        },
      ],
    },
    {
      id: "b4",
      index: "B4",
      title: "B4 Pollution (If applicable)",
      icon: AlertTriangle,
      fields: [
        {
          key: "b4MonitoringProcess",
          id: "story-environment-b4-monitoring-process",
          label: "How do you monitor pollution risks or compliance? (Briefly explain frequency, responsible person, or type of monitoring)",
          placeholder: "e.g. Monthly water discharge testing by our facilities manager. Annual air quality assessment by certified external auditor. Quarterly review of hazardous materials storage compliance...",
        },
        {
          key: "b4PlannedImprovements",
          id: "story-environment-b4-planned-improvements",
          label: "What improvements or initiatives are planned to reduce pollution risks in the next 12-24 months?",
          placeholder: "e.g. Installing new wastewater filtration system by Q2 2026. Transitioning to water-based paints to reduce VOC emissions. Upgrading chemical storage to double-containment systems...",
        },
      ],
    },
    {
      id: "b5",
      index: "B5",
      title: "B5 Biodiversity",
      icon: Sprout,
      fields: [
        {
          key: "b5MaterialityExplanation",
          id: "story-environment-b5-materiality-explanation",
          label: "Explain why biodiversity is (or is not) a material topic for your business (mention your physical footprint/land use).",
          placeholder: "e.g. Biodiversity has limited materiality as we operate from urban industrial estates with no proximity to protected areas. Our 2-hectare site is fully developed with minimal green space...",
        },
        {
          key: "b5ValueChainImpacts",
          id: "story-environment-b5-value-chain-impacts",
          label: "Are there any notable biodiversity impacts in your value chain (e.g., raw material sourcing, packaging)?",
          placeholder: "e.g. We source timber packaging materials - working with FSC-certified suppliers to ensure sustainable forestry. Palm oil derivatives in products being transitioned to RSPO-certified sources...",
        },
        {
          key: "b5ActionsToReduce",
          id: "story-environment-b5-actions-to-reduce",
          label: "What actions have you taken (or do you take routinely) to avoid or reduce biodiversity impacts?",
          placeholder: "e.g. All packaging suppliers must provide sustainability certifications. Created wildflower pollinator areas on 15% of site grounds. Annual biodiversity assessment of supply chain...",
        },
      ],
    },
    {
      id: "b6",
      index: "B6",
      title: "B6 Water",
      icon: Droplets,
      fields: [
        {
          key: "b6WaterUseDescription",
          id: "story-environment-b6-water-use-description",
          label: "Describe how your business uses water, where the main water use happens, where it comes from, and where wastewater goes.",
          placeholder: "e.g. Primary water use is for cooling systems in manufacturing (65%) and sanitation/facilities (35%). Water sourced from municipal supply. Cooling water recirculated;\r\n\r\n wastewater discharged to municipal system after on-site treatment...",
        },
        {
          key: "b6WaterIssues",
          id: "story-environment-b6-water-issues",
          label: "Any water-related issues during the year? (shortages, quality problems, leaks, complaints)",
          placeholder: "e.g. Minor leak detected in cooling system in March, repaired within 48 hours with estimated 2,000L loss. No supply shortages or quality issues. No regulatory non-compliances...",
        },
        {
          key: "b6WaterImprovements",
          id: "story-environment-b6-water-improvements",
          label: "What actions are in place (or planned) to reduce water use or improve water management?",
          placeholder: "e.g. Installing closed-loop cooling system to reduce consumption by 40%. Rainwater harvesting system planned for 2026. Monthly monitoring and leak detection program implemented...",
        },
      ],
    },
    {
      id: "b7",
      index: "B7",
      title: "B7 Circular Economy & Waste",
      icon: Recycle,
      fields: [
        {
          key: "b7WasteManagement",
          id: "story-environment-b7-waste-management",
          label: "Describe the main types/sources of waste in your operations and how you manage waste today (prevention, reuse, recycling, disposal).",
          placeholder: "e.g. Main waste streams: cardboard packaging (40%), metal offcuts (30%), general office waste (20%), plastics (10%). Cardboard and metals recycled through certified contractors. Implementing waste segregation at source. Current recycling rate 65%...",
        },
        {
          key: "b7HazardousWaste",
          id: "story-environment-b7-hazardous-waste",
          label: "Do you generate any hazardous or special waste? If yes: how it is handled and disposed of.",
          placeholder: "e.g. Small quantities of solvent-contaminated rags (approx. 200kg/year) and used oil (~150L/year). All stored in designated locked containers and collected quarterly by licensed hazardous waste contractor (permit #12345)...",
        },
        {
          key: "b7CircularityImprovements",
          id: "story-environment-b7-circularity-improvements",
          label: "What improvements or initiatives are planned to reduce waste or increase circularity in the next 12-24 months?",
          placeholder: "e.g. Target 80% recycling rate by end 2026. Partnering with supplier to take back packaging for reuse. Implementing material optimization software to reduce offcuts by 15%. Exploring composting for organic waste...",
        },
      ],
    },
    {
      id: "c4",
      index: "C4",
      title: "C4 Climate Risks",
      icon: AlertTriangle,
      fields: [
        {
          key: "c4PhysicalRisks",
          id: "story-environment-c4-physical-risks",
          label: "What is the main climate-related physical risks to your operations/value chain? (e.g., heat, flooding, drought, storms)",
          placeholder: "e.g. Our main physical risk is flooding - our warehouse is in a flood zone with 1-in-100 year flood risk. Summer heat waves disrupt production efficiency and increase cooling costs...",
        },
        {
          key: "c4TransitionRisks",
          id: "story-environment-c4-transition-risks",
          label: "What are the main transition risks or opportunities? (e.g., energy prices, regulation, customer requirements)",
          placeholder: "e.g. Rising carbon pricing and energy costs pose financial risk - estimated 15% increase in operational costs by 2028. Opportunity: growing customer demand for low-carbon products could increase revenue by 20%...",
        },
        {
          key: "c4RiskAssessment",
          id: "story-environment-c4-risk-assessment",
          label: "How are these risks assessed/managed?",
          placeholder: "e.g. Annual climate risk assessment conducted by sustainability team. Quarterly review by board. Scenario analysis for 2030 and 2050 using IPCC projections. Climate risks integrated into enterprise risk register...",
        },
        {
          key: "c4AdaptationActions",
          id: "story-environment-c4-adaptation-actions",
          label: "What adaptation actions are taken or planned?",
          placeholder: "e.g. Installing flood barriers and raising electrical equipment. Diversifying supplier base geographically to reduce climate-related supply disruption. Heat resilience measures: improved ventilation and heat management systems...",
        },
      ],
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <CardPanelHeader
        title={"Environment"}
        description={"Narrative fields for B3 Energy & GHG, B4 Pollution, B5 Biodiversity, B6 Water, B7 Waste, C3 Targets, C4 Climate Risks."}
        onClose={onCollapse}
        onSave={() => {
          void forceSave();
        }}
        saveStatus={saveStatus}
        disabled={disabled}
        tag={"Sustainability Story"}
      />

      {disabled && <LockedBanner />}

      {narrativeSections.slice(0, 3).map((section) => (
        <SectionCard
          key={section.id}
          header={
            <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[20px] bg-primary/10 text-primary">
                  <section.icon className="h-5 w-5" />
                </div>
                <div className="space-y-1.5">
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="text-xl font-semibold leading-7 text-slate-900">{section.title}</h3>
                  </div>
                  {section.description ? (
                    <p className="max-w-3xl text-sm leading-6 text-slate-500">{section.description}</p>
                  ) : null}
                </div>
              </div>
            </div>
          }
        >
          <div className="grid gap-4">
            {section.fields?.map((field) => (
              <VoiceTextareaField
                key={field.key}
                id={field.id}
                label={field.label}
                placeholder={field.placeholder}
                value={data[field.key]}
                rows={field.rows}
                disabled={disabled}
                onChange={(value) => update({ [field.key]: value })}
              />
            ))}
          </div>
        </SectionCard>
      ))}

      <SectionCard
        header={
          <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[20px] bg-primary/10 text-primary">
                <Target className="h-5 w-5" />
              </div>
              <div className="space-y-1.5">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="text-xl font-semibold leading-7 text-slate-900">
                    {"C3 Targets & Transition Actions"}
                  </h3>
                </div>
              </div>
            </div>
          </div>
        }
      >
        <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 sm:p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm font-semibold text-slate-900">{"List up to 3 sustainability targets you are committed to (Target + baseline + deadline)"}</p>
            {data.c3Targets.length < 3 ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addTarget}
                disabled={disabled}
                className="rounded-2xl border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 shadow-none hover:bg-slate-100"
              >
                <Plus className="mr-2 h-4 w-4" />
                {"Add Target"}
              </Button>
            ) : null}
          </div>

          {data.c3Targets.length === 0 ? (
            <div className="mt-4 rounded-[20px] border border-dashed border-slate-300 bg-white px-5 py-8 text-center">
              <p className="text-sm leading-6 text-slate-500">{"No targets added yet. Click \"Add Target\" to add your first sustainability target."}</p>
            </div>
          ) : (
            <div className="mt-5 grid gap-4">
              {data.c3Targets.map((target, index) => (
                <div key={target.id} className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                  <div className="mb-4 flex items-center justify-between gap-4">
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.12em] text-slate-400">
                        {`Target ${index + 1}`}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeTarget(target.id)}
                      disabled={disabled}
                      className="h-9 w-9 rounded-2xl text-destructive hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="grid gap-4 md:grid-cols-3">
                    <TargetTextField
                      id={`story-environment-c3-target-${target.id}-target`}
                      label={"Target (What)"}
                      placeholder={"e.g. Reduce Scope 1 & 2 emissions by 30%"}
                      value={target.target}
                      disabled={disabled}
                      onChange={(value) => updateTarget(target.id, { target: value })}
                    />
                    <TargetTextField
                      id={`story-environment-c3-target-${target.id}-baseline`}
                      label={"Baseline (Current status)"}
                      placeholder={"e.g. 450 tCO2e (2025 baseline)"}
                      value={target.baseline}
                      disabled={disabled}
                      onChange={(value) => updateTarget(target.id, { baseline: value })}
                    />
                    <TargetTextField
                      id={`story-environment-c3-target-${target.id}-deadline`}
                      label={"Deadline (When)"}
                      placeholder={"e.g. December 2028"}
                      value={target.deadline}
                      disabled={disabled}
                      onChange={(value) => updateTarget(target.id, { deadline: value })}
                    />
                  </div>

                  <div className="mt-4 grid gap-4">
                    <VoiceTextareaField
                      id={`story-environment-c3-target-${target.id}-transition-actions`}
                      label={"Transition Actions (How)"}
                      placeholder={"e.g. Install 100kW solar array;\r\n\r\n transition fleet to electric;\r\n\r\n improve building insulation..."}
                      value={target.transitionActions}
                      rows={3}
                      disabled={disabled}
                      onChange={(value) => updateTarget(target.id, { transitionActions: value })}
                    />
                    <VoiceTextareaField
                      id={`story-environment-c3-target-${target.id}-responsibility-tracking`}
                      label={"Responsibility & Tracking"}
                      placeholder={"e.g. COO responsible;\r\n\r\n monthly emissions tracking via energy bills;\r\n\r\n quarterly board review..."}
                      value={target.responsibilityTracking}
                      rows={3}
                      disabled={disabled}
                      onChange={(value) => updateTarget(target.id, { responsibilityTracking: value })}
                    />
                    <VoiceTextareaField
                      id={`story-environment-c3-target-${target.id}-constraints-risks`}
                      label={"Constraints & Risks"}
                      placeholder={"e.g. Solar installation dependent on roof structural assessment and grid connection approval. Mitigate by starting permitting early..."}
                      value={target.constraintsRisks}
                      rows={3}
                      disabled={disabled}
                      onChange={(value) => updateTarget(target.id, { constraintsRisks: value })}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </SectionCard>

      {narrativeSections.slice(3).map((section) => (
        <SectionCard
          key={section.id}
          header={
            <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[20px] bg-primary/10 text-primary">
                  <section.icon className="h-5 w-5" />
                </div>
                <div className="space-y-1.5">
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="text-xl font-semibold leading-7 text-slate-900">{section.title}</h3>
                  </div>
                  {section.description ? (
                    <p className="max-w-3xl text-sm leading-6 text-slate-500">{section.description}</p>
                  ) : null}
                </div>
              </div>
            </div>
          }
        >
          <div className="grid gap-4">
            {section.fields?.map((field) => (
              <VoiceTextareaField
                key={field.key}
                id={field.id}
                label={field.label}
                placeholder={field.placeholder}
                value={data[field.key]}
                rows={field.rows}
                disabled={disabled}
                onChange={(value) => update({ [field.key]: value })}
              />
            ))}
          </div>
        </SectionCard>
      ))}

      <ProgressCard completion={storyEnvironmentCompletion} />
    </div>
  );
}
